"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Check, ChevronDown, Copy, QrCode, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { TokenGlyph } from "@/components/ui/token-icons";
import { useRolledValue } from "@/components/moonshot/moonshot-screen";

interface AddFundsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Fund your account: one screen, top to bottom (the Add Funds v7 layout in
 * our look). The first deposit match on gold, opening to an amount picker and
 * "Deposit + Promo = You play with"; then Crypto or Card. Crypto: the token
 * and network you send, the deposit address with its QR and Copy, the fee,
 * what lands and how fast, and the one warning. Card: coming soon.
 *
 * MOCK: the addresses, the match terms, fees and timings are placeholders;
 * nothing is sent or credited. Real per-user addresses, the swap to USDC and
 * the match are backend work.
 */

interface Token {
  id: string;
  sym: string;
  name: string;
  /** Networks this token can be sent on (ids in NETWORKS). */
  networks: string[];
}
interface Network {
  id: string;
  name: string;
  fee: string;
  arrives: string;
}

const NETWORKS: Network[] = [
  { id: "base", name: "Base", fee: "< $0.10", arrives: "< 1 min" },
  { id: "ethereum", name: "Ethereum", fee: "~ $2", arrives: "~ 3 min" },
  { id: "solana", name: "Solana", fee: "< $0.01", arrives: "< 1 min" },
  { id: "polygon", name: "Polygon", fee: "< $0.05", arrives: "< 1 min" },
  { id: "arbitrum", name: "Arbitrum", fee: "< $0.10", arrives: "< 1 min" },
  { id: "bnb-chain", name: "BNB Chain", fee: "< $0.10", arrives: "< 1 min" },
];

const ALL = NETWORKS.map((n) => n.id);
const TOKENS: Token[] = [
  { id: "usdc", sym: "USDC", name: "USD Coin", networks: ALL },
  { id: "usdt", sym: "USDT", name: "Tether USD", networks: ALL },
  { id: "eth", sym: "ETH", name: "Ether", networks: ["base", "ethereum", "arbitrum"] },
  { id: "weth", sym: "WETH", name: "Wrapped Ether", networks: ["base", "ethereum", "polygon", "arbitrum"] },
  { id: "bnb", sym: "BNB", name: "BNB", networks: ["bnb-chain"] },
  { id: "sol", sym: "SOL", name: "Solana", networks: ["solana"] },
];

const EVM_ADDRESS = "0xd132A67B0f0C1a49E171584D14F114ac8a5aEdbD";
const SOLANA_ADDRESS = "7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU";

/** The first deposit match: deposit is matched 100%, up to $100 of promo. */
const MATCH_CAP = 100;
const AMOUNTS = [25, 50, 100, 250];
/** MOCK: the promo unlocks after this many times its amount in settled entries, within the days below. */
const UNLOCK_MULTIPLE = 3;
const UNLOCK_DAYS = 6;

const TERMS_URL = "https://www.getpicks.app/terms-and-conditions";
const HOW_URL = "https://www.getpicks.app/help-center";

type Tab = "crypto" | "card";

export function AddFundsModal({ open, onOpenChange }: AddFundsModalProps) {
  const [tab, setTab] = useState<Tab>("crypto");
  const [promoOpen, setPromoOpen] = useState(false);
  const [amount, setAmount] = useState(50);
  const [tokenId, setTokenId] = useState("usdc");
  const [networkId, setNetworkId] = useState("base");
  const [picker, setPicker] = useState<"token" | "network" | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notified, setNotified] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout>>();

  const token = TOKENS.find((t) => t.id === tokenId) ?? TOKENS[0];
  const network = NETWORKS.find((n) => n.id === networkId) ?? NETWORKS[0];
  const address = network.id === "solana" ? SOLANA_ADDRESS : EVM_ADDRESS;
  const promo = Math.min(amount, MATCH_CAP);

  // Every opening starts fresh on Crypto, USDC on Base, the match folded.
  useEffect(() => {
    if (!open) return;
    setTab("crypto");
    setPromoOpen(false);
    setPicker(null);
    setQrOpen(false);
    setCopied(false);
  }, [open]);
  useEffect(() => () => clearTimeout(copyTimer.current), []);

  function chooseToken(id: string) {
    const next = TOKENS.find((t) => t.id === id) ?? TOKENS[0];
    setTokenId(id);
    // A network the new token cannot use falls back to its first one.
    if (!next.networks.includes(networkId)) setNetworkId(next.networks[0]);
    setPicker(null);
  }

  function copyAddress() {
    try {
      void navigator.clipboard?.writeText(address);
    } catch {
      // Clipboard unavailable: the address stays on screen to copy by hand.
    }
    setCopied(true);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1800);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showClose={false}
        className="max-h-[92dvh] max-w-[420px] gap-0 overflow-y-auto rounded-[28px] border-white/[0.08] bg-background p-0 scrollbar-hide max-sm:rounded-b-none"
      >
        {/* Header: wordmark, title, close. */}
        <div className="flex items-start justify-between gap-3 border-b border-white/[0.06] px-5 pb-4 pt-5">
          <div>
            <Image src="/getpicks-logo-white.svg" alt="GETPICKS" width={92} height={14} className="h-3 w-auto" />
            <DialogTitle className="mt-2 font-open-sauce text-[26px] font-black leading-tight tracking-tight text-white">Fund your account.</DialogTitle>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-white ring-1 ring-inset ring-white/10 transition-colors duration-200 hover:bg-white/[0.1]"
          >
            <X className="size-4" strokeWidth={2.5} />
          </button>
        </div>

        <div className="flex flex-col gap-3 px-5 pb-5 pt-4">
          {/* The first deposit match, folded to one line until "See how". */}
          <section className="rounded-[20px] bg-gold p-3.5 text-gold-foreground">
            <div className="flex items-center gap-3">
              <span className="font-open-sauce text-[30px] font-black leading-none tracking-tight">100%</span>
              <span className="min-w-0 flex-1">
                <span className="block font-open-sauce text-[15px] font-black leading-tight tracking-tight">First deposit match</span>
                <span className="block text-[12px] font-semibold tracking-tight text-gold-foreground/75">Up to ${MATCH_CAP} in promo</span>
              </span>
              <button
                type="button"
                onClick={() => setPromoOpen((v) => !v)}
                aria-expanded={promoOpen}
                className="flex h-8 shrink-0 items-center gap-1 rounded-full bg-gold-foreground/10 px-3 text-[13px] font-semibold tracking-tight transition-colors duration-200 hover:bg-gold-foreground/15"
              >
                {promoOpen ? "Hide" : "See how"}
                <ChevronDown className={cn("size-3.5 transition-transform duration-300", promoOpen && "rotate-180")} strokeWidth={2.5} />
              </button>
            </div>

            {/* Opens by height, so nothing below jumps. */}
            <div className={cn("grid transition-[grid-template-rows,opacity] duration-300 ease-out", promoOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}>
              <div className="overflow-hidden">
                <Segmented
                  options={AMOUNTS.map((a) => ({ id: String(a), label: `$${a}` }))}
                  value={String(amount)}
                  onChange={(id) => setAmount(Number(id))}
                  className="mt-3 bg-gold-foreground/10"
                  pill="bg-gold-foreground"
                  on="text-gold"
                  off="text-gold-foreground"
                />
                <MatchSum deposit={amount} promo={promo} />
                <p className="mt-2.5 text-[12px] leading-snug tracking-tight text-gold-foreground/80">
                  Unlocks after ${promo * UNLOCK_MULTIPLE} in settled lineup entries within {UNLOCK_DAYS} days.{" "}
                  <a href={HOW_URL} target="_blank" rel="noreferrer" className="whitespace-nowrap font-semibold text-gold-foreground underline underline-offset-2">
                    How it works
                  </a>
                </p>
              </div>
            </div>
          </section>

          <Segmented
            options={[
              { id: "crypto", label: "Crypto" },
              { id: "card", label: "Card" },
            ]}
            value={tab}
            onChange={(id) => setTab(id as Tab)}
            className="bg-white/[0.04] ring-1 ring-inset ring-white/[0.06]"
            pill="bg-white"
            on="text-grey-950"
            off="text-grey-300"
            tall
          />

          {tab === "crypto" ? (
            <div key="crypto" className="flex flex-col gap-3 animate-in fade-in slide-in-from-bottom-1 duration-300">
              {/* What you send. */}
              <Panel title="You send" note={`${token.networks.length} network${token.networks.length === 1 ? "" : "s"} for ${token.sym}`}>
                <div className="relative grid grid-cols-2 gap-2 p-3">
                  <Select label="Token" value={token.sym} glyph={token.id} open={picker === "token"} onClick={() => setPicker(picker === "token" ? null : "token")} />
                  <Select label="Network" value={network.name} glyph={network.id} open={picker === "network"} onClick={() => setPicker(picker === "network" ? null : "network")} />
                  {picker && (
                    <>
                      {/* A tap anywhere else closes the list. */}
                      <button type="button" aria-label="Close list" className="fixed inset-0 z-10 cursor-default" onClick={() => setPicker(null)} />
                      <div
                        className={cn(
                          "absolute top-[calc(100%-6px)] z-20 w-[calc(50%-16px)] overflow-hidden rounded-2xl bg-grey-900 p-1.5 shadow-[0_16px_40px_hsl(0_0%_0%/0.55)] ring-1 ring-white/10 animate-in fade-in zoom-in-95 slide-in-from-top-1 duration-200",
                          picker === "token" ? "left-3 origin-top-left" : "right-3 origin-top-right",
                        )}
                      >
                        {picker === "token"
                          ? TOKENS.map((t) => (
                              <Option key={t.id} glyph={t.id} label={t.sym} sub={t.name} on={t.id === token.id} onClick={() => chooseToken(t.id)} />
                            ))
                          : NETWORKS.filter((n) => token.networks.includes(n.id)).map((n) => (
                              <Option
                                key={n.id}
                                glyph={n.id}
                                label={n.name}
                                on={n.id === network.id}
                                onClick={() => {
                                  setNetworkId(n.id);
                                  setPicker(null);
                                }}
                              />
                            ))}
                      </div>
                    </>
                  )}
                </div>
              </Panel>

              {/* Where to send it. */}
              <Panel title="Your deposit address" note={`${token.sym} · ${network.name}`}>
                <div className="p-3.5">
                  <div className="flex items-center gap-3">
                    <span key={address} className="min-w-0 flex-1 break-all font-open-sauce text-[16px] font-black leading-snug tracking-tight text-white animate-in fade-in duration-300">
                      {address}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQrOpen((v) => !v)}
                      aria-expanded={qrOpen}
                      className="flex size-[60px] shrink-0 flex-col items-center justify-center rounded-xl bg-white text-grey-950 transition-[filter] duration-200 hover:brightness-95"
                    >
                      <span className="font-open-sauce text-[13px] font-black leading-none">QR</span>
                      <span className="mt-1 text-[9px] font-semibold leading-none text-grey-700">{qrOpen ? "Tap to hide" : "Tap to scan"}</span>
                    </button>
                  </div>

                  {/* The code, opened in place. MOCK: a glyph, not a scannable code. */}
                  <div className={cn("grid transition-[grid-template-rows,opacity] duration-300 ease-out", qrOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}>
                    <div className="overflow-hidden">
                      <div className="mx-auto mt-3 flex size-44 items-center justify-center rounded-2xl bg-white">
                        <QrCode className="size-36 text-grey-950" strokeWidth={1.25} />
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={copyAddress}
                    className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary font-open-sauce text-[15px] font-black tracking-tight text-primary-foreground transition-[filter,transform] duration-150 hover:brightness-110 active:scale-[0.99]"
                  >
                    <span key={String(copied)} className="flex items-center gap-2 animate-in fade-in zoom-in-95 duration-200">
                      {copied ? <Check className="size-4" strokeWidth={3} /> : <Copy className="size-4" />}
                      {copied ? "Copied" : "Copy address"}
                    </span>
                  </button>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Fact label="Fee" value={network.fee} tone="text-win" />
                    <Fact label="Lands" value={token.id === "usdc" ? "USDC 1:1" : "as USDC"} />
                    <Fact label="Arrives" value={network.arrives} />
                  </div>
                </div>
              </Panel>

              {/* The one warning, and the small print. */}
              <p className="mt-1 flex items-center justify-center gap-2 text-center text-[13px] tracking-tight text-grey-300">
                <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-gold" />
                <span>
                  Only send <b className="font-semibold text-white">{token.sym}</b> on <b className="font-semibold text-white">{network.name}</b> to this address.
                </span>
              </p>
              <p className="-mt-1.5 text-center text-[12px] tracking-tight text-grey-400">
                18+ ·{" "}
                <a href={TERMS_URL} target="_blank" rel="noreferrer" className="underline underline-offset-2 transition-colors hover:text-white">
                  Terms apply
                </a>
              </p>
            </div>
          ) : (
            <div key="card" className="flex flex-col items-center px-4 py-8 text-center animate-in fade-in slide-in-from-bottom-1 duration-300">
              <p className="font-open-sauce text-[22px] font-black leading-tight tracking-tight text-white">Coming soon to your area.</p>
              <p className="mt-1.5 text-[14px] tracking-tight text-grey-300">Card deposits are rolling out region by region.</p>
              <button
                type="button"
                onClick={() => setNotified(true)}
                disabled={notified}
                className={cn(
                  "mt-5 flex h-11 items-center gap-2 rounded-full px-6 font-open-sauce text-[15px] font-black tracking-tight transition-[filter,background-color,color] duration-300",
                  notified ? "bg-white/[0.06] text-white" : "bg-primary text-primary-foreground hover:brightness-110",
                )}
              >
                {notified && <Check className="size-4 animate-in zoom-in-50 duration-200" strokeWidth={3} />}
                {notified ? "We'll let you know" : "Notify me when live"}
              </button>
            </div>
          )}

          <p className="border-t border-white/[0.06] pt-3 text-center text-[11px] tracking-tight text-grey-400">Protected by Privy</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =============================================================================
   Pieces
   ============================================================================= */

/** Options on a track, the chosen one on a pill that slides to it. */
function Segmented({
  options,
  value,
  onChange,
  className,
  pill,
  on,
  off,
  tall = false,
}: {
  options: { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
  pill: string;
  on: string;
  off: string;
  tall?: boolean;
}) {
  const index = Math.max(0, options.findIndex((o) => o.id === value));
  const width = 100 / options.length;
  return (
    <div className={cn("rounded-full p-1", className)}>
      <div className="relative flex">
      <span
        aria-hidden
        className={cn("absolute inset-y-0 rounded-full transition-[left] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]", pill)}
        style={{ left: `${index * width}%`, width: `${width}%` }}
      />
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={o.id === value}
          onClick={() => onChange(o.id)}
          className={cn(
            "relative flex-1 rounded-full font-open-sauce font-black tracking-tight transition-colors duration-300",
            tall ? "h-10 text-[15px]" : "h-8 text-[14px]",
            o.id === value ? on : off,
          )}
        >
          {o.label}
        </button>
      ))}
      </div>
    </div>
  );
}

/** Deposit + Promo = You play with, each figure rolling to its new value. */
function MatchSum({ deposit, promo }: { deposit: number; promo: number }) {
  const d = useRolledValue(deposit, 420);
  const p = useRolledValue(promo, 420);
  const t = useRolledValue(deposit + promo, 420);
  const money = (n: number) => `$${Math.round(n)}`;
  return (
    <div className="mt-3 grid grid-cols-[auto_1fr_auto_1fr_auto] items-end gap-x-2">
      <Figure label="Deposit" value={money(d)} />
      <span className="pb-1 text-center text-[15px] font-semibold text-gold-foreground/60">+</span>
      <Figure label="Promo" value={money(p)} />
      <span className="pb-1 text-center text-[15px] font-semibold text-gold-foreground/60">=</span>
      <Figure label="You play with" value={money(t)} end />
    </div>
  );
}

function Figure({ label, value, end = false }: { label: string; value: string; end?: boolean }) {
  return (
    <span className={cn("flex flex-col", end && "items-end")}>
      <span className="text-[12px] font-semibold tracking-tight text-gold-foreground/75">{label}</span>
      <span className="font-open-sauce text-[20px] font-black leading-tight tabular-nums tracking-tight">{value}</span>
    </span>
  );
}

/** A dark section: a titled head with a note on the right, then its body. */
function Panel({ title, note, children }: { title: string; note: string; children: React.ReactNode }) {
  return (
    <section className="rounded-[20px] bg-white/[0.03] ring-1 ring-inset ring-white/[0.06]">
      <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-3.5 py-2.5">
        <span className="font-open-sauce text-[14px] font-black tracking-tight text-white">{title}</span>
        <span key={note} className="truncate text-[12px] tracking-tight text-grey-400 animate-in fade-in duration-300">
          {note}
        </span>
      </div>
      {children}
    </section>
  );
}

/** A picker's face: glyph, small label over the value, a chevron that turns when open. */
function Select({ label, value, glyph, open, onClick }: { label: string; value: string; glyph: string; open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      className={cn(
        "flex h-14 min-w-0 items-center gap-2.5 rounded-2xl bg-white/[0.04] px-3 text-left ring-1 ring-inset transition-colors duration-200 hover:bg-white/[0.07]",
        open ? "ring-white/25" : "ring-white/[0.06]",
      )}
    >
      <TokenGlyph id={glyph} size={28} />
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] tracking-tight text-grey-400">{label}</span>
        <span key={value} className="block truncate font-open-sauce text-[15px] font-black leading-tight tracking-tight text-white animate-in fade-in duration-200">
          {value}
        </span>
      </span>
      <ChevronDown className={cn("size-4 shrink-0 text-grey-400 transition-transform duration-300", open && "rotate-180")} />
    </button>
  );
}

function Option({ glyph, label, sub, on, onClick }: { glyph: string; label: string; sub?: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn("flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors duration-150 hover:bg-white/[0.06]", on && "bg-white/[0.06]")}
    >
      <TokenGlyph id={glyph} size={24} />
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold leading-tight tracking-tight text-white">{label}</span>
        {sub && <span className="block truncate text-[11px] tracking-tight text-grey-400">{sub}</span>}
      </span>
      {on && <Check className="size-4 shrink-0 text-white" strokeWidth={3} />}
    </button>
  );
}

/** A small fact under the address: "Fee < $0.10". */
function Fact({ label, value, tone = "text-white" }: { label: string; value: string; tone?: string }) {
  return (
    <span className="flex h-8 items-center gap-1.5 rounded-full bg-white/[0.04] px-3 text-[13px] tracking-tight ring-1 ring-inset ring-white/[0.06]">
      <span className="text-grey-400">{label}</span>
      <span key={value} className={cn("font-semibold animate-in fade-in duration-200", tone)}>
        {value}
      </span>
    </span>
  );
}
